import { useLocation } from "react-router-dom";
import BottomPanel from "../../components/app/panels/BottomPanel";
import {
  dismissInstallSheet,
  installOriginFor,
  installSheetDismissedStore,
} from "../../domains/app/appInstallWall";
import { useStore } from "../../lib/store";
import { gaEventInstallWallDismissed } from "../../tools/analytics/domains/installWall.analytics";
import type { InstallWallDismissMethod } from "../../tools/analytics/domains/installWall.analytics";
import AppInstall from "./AppInstall";

/**
 * The install ask, over the article the reader came for.
 *
 * Reddit's shape, and for Reddit's reason: somebody followed a link to read one
 * thing, so they get that thing, and the ask arrives on top of it instead of in
 * place of it. The earlier full-page version made a shared link look broken
 * before it looked like an invitation.
 *
 * Dismissal lives in a MEMORY store (`installSheetDismissedStore`), which is
 * the whole behaviour: it dies with the document, so the sheet returns on the
 * next fresh load, and it survives client-side navigation, so a shared playlist
 * does not ask once per article.
 *
 * The body is `AppInstall`, which reads the escape below and says something
 * TRUE for it: over a page the reader is already reading, the ask is an offer
 * with a door, not "install to continue".
 */
export default function InstallBottomSheet() {
  const [isDismissed] = useStore(installSheetDismissedStore);
  const { pathname } = useLocation();

  function leave(method: InstallWallDismissMethod): void {
    gaEventInstallWallDismissed({
      origin: installOriginFor(pathname),
      method,
    });
    dismissInstallSheet();
  }

  return (
    <BottomPanel
      isOpen={!isDismissed}
      // Explicit, because this sheet must be dismissible on purpose. The drag
      // handle alone reads as decoration to a reader who has never seen the
      // app, and a reader who cannot find the way out of the ask has been
      // walled after all — which is the thing this shape exists to avoid.
      displayCloseButton
      onClose={() => {
        leave("close");
      }}
    >
      <AppInstall
        // The door this shape exists for. The X alone is a way out that the
        // copy never mentions; this one is worded, so a reader who wants the
        // article rather than the app can see that they may have it.
        escape={{
          kind: "continue-in-browser",
          onContinue: () => {
            leave("continue-in-browser");
          },
        }}
      />
    </BottomPanel>
  );
}
